import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { VideoRetentionData } from "@/screens/creatives/types";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer } from "recharts";

export interface VideoRetentionGraphProps {
  data: VideoRetentionData;
  isVisible: boolean;
  isHovered: boolean;
  videoProgress: number; // 0-100 percentage of video played
}

const VideoRetentionGraph: React.FC<VideoRetentionGraphProps> = ({
  data,
  isVisible,
  isHovered,
  videoProgress,
}) => {
  // Sort data by percentage and add starting point if needed
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Sort by percentage
    const sorted = [...data].sort((a, b) => a.percentage - b.percentage);

    // Always start with 100% retention at 0% of video
    const processedData = [{ percentage: 0, retention: 100 }];

    sorted.forEach((point) => {
      if (point.percentage > 0) {
        processedData.push(point);
      }
    });

    // Ensure we end at 100% of video
    if (processedData[processedData.length - 1]?.percentage !== 100) {
      const lastRetention = processedData[processedData.length - 1]?.retention ?? 0;
      processedData.push({ percentage: 100, retention: lastRetention });
    }

    return processedData;
  }, [data]);

  // Generate unique IDs to avoid conflicts when multiple graphs are rendered
  const uniqueId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  if (!isVisible || chartData.length < 2) return null;

  return (
    <div
      className={cn(
        "absolute bottom-[48px] px-3 left-0 right-0 h-[60px] z-10 pointer-events-none transition-opacity duration-500",
        isHovered ? "opacity-100" : "opacity-0",
      )}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            {/* Lighter gradient for unplayed portion */}
            <linearGradient
              id={`retentionGradient-${uniqueId}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.2)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0.02)" />
            </linearGradient>
            {/* Darker gradient for played portion */}
            <linearGradient
              id={`retentionGradientPlayed-${uniqueId}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0.15)" />
            </linearGradient>
            {/* Clip path for played portion */}
            <clipPath id={`playedClip-${uniqueId}`}>
              <rect x="0" y="0" width={`${videoProgress}%`} height="100%" />
            </clipPath>
          </defs>
          <XAxis
            dataKey="percentage"
            type="number"
            domain={[0, 100]}
            hide
            padding={{ left: 0, right: 0 }}
          />
          <YAxis type="number" domain={[0, 100]} hide padding={{ top: 4, bottom: 0 }} />
          {/* Base area (unplayed - lighter) */}
          <Area
            type="monotone"
            dataKey="retention"
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth={1.5}
            fill={`url(#retentionGradient-${uniqueId})`}
            isAnimationActive={false}
          />
          {/* Overlaid area (played portion - darker) */}
          <Area
            type="monotone"
            dataKey="retention"
            stroke="rgba(255, 255, 255, 0.9)"
            strokeWidth={1.5}
            fill={`url(#retentionGradientPlayed-${uniqueId})`}
            isAnimationActive={false}
            clipPath={`url(#playedClip-${uniqueId})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VideoRetentionGraph;
